import { IconButton } from "../button/Button";
import type { JSX, VoidComponent } from "solid-js";
import { useTranslation } from "../../state/i18n";
import { Pagination as ArkPagination } from "@ark-ui/solid";
import { clamp } from "lodash-es";

export type PaginationProps = {
  initialPage: number;
  onPageChange: (page: number) => void;
  totalElements: number;
  pageSize: number;
};

const Pagination: VoidComponent<PaginationProps> = (props) => {
  const { t } = useTranslation();
  return (
    <>
      <ArkPagination.Root
        defaultPage={props.initialPage}
        count={props.totalElements}
        pageSize={props.pageSize}
        onPageChange={(details) => props.onPageChange(details.page)}
        class="flex w-44 items-center justify-start gap-2"
      >
        <ArkPagination.PrevTrigger
          asChild={(parentProps) => (
            <IconButton
              {...parentProps}
              icon="chevronLeft"
              variant="tertiary"
              size="medium"
              aria-label={t(($) => $.PAGINATION_PREVIOUS_PAGE)}
            />
          )}
        />
        <ArkPagination.Context>
          {(context) => (
            <div class="flex grow items-center justify-center gap-3">
              <div class="w-10">
                <PageNumberInput
                  value={context().page}
                  onBlur={(event) => {
                    const value = Number.parseInt(event.target.value);
                    const clampedValue = clamp(value, 1, context().totalPages);

                    // If the new value is invalid or the same as what is stored in the context, then only update the UI
                    // directly. Since setting the same value in the context won't trigger a UI update.
                    if (!Number.isFinite(clampedValue) || clampedValue === context().page) {
                      event.target.value = context().page.toString();
                    } else {
                      context().setPage(clampedValue);
                    }
                  }}
                  minValue={1}
                  maxValue={context().totalPages}
                  label={t(($) => $.PAGINATION_PAGE_NUMBER)}
                />
              </div>
              <span class="text-base-regular text-secondary">{t(($) => $.PAGINATION_OF)}</span>
              <span class="text-base-regular text-secondary">
                {context().totalPages < 10000 ? Math.max(context().totalPages, 1) : "9999+"}
              </span>
            </div>
          )}
        </ArkPagination.Context>
        <ArkPagination.NextTrigger
          asChild={(parentProps) => (
            <IconButton
              {...parentProps}
              icon="chevronRight"
              variant="tertiary"
              size="medium"
              aria-label={t(($) => $.PAGINATION_NEXT_PAGE)}
            />
          )}
        />
      </ArkPagination.Root>
    </>
  );
};

type PageNumberInputProps = {
  value: number | null;
  onInput?: JSX.InputEventHandler<HTMLInputElement, InputEvent>;
  onBlur: JSX.FocusEventHandler<HTMLInputElement, FocusEvent>;
  minValue: number;
  maxValue: number;
  label: string;
};

const PageNumberInput: VoidComponent<PageNumberInputProps> = (props) => (
  <input
    id="page"
    min={props.minValue}
    max={props.maxValue}
    type="number"
    class="no-arrows w-full rounded-sm border border-default bg-surface-default p-1 text-center text-base-regular text-primary focus-visible:outline-default"
    value={props.value ?? ""}
    onKeyDown={(e) => {
      if (
        e.key.length === 1 &&
        isNaN(Number(e.key)) &&
        !["Backspace", "Delete", "Tab", "Escape", "Enter", ".", ",", "-"].includes(e.key) &&
        !(e.key === "a" && (e.metaKey || e.ctrlKey)) &&
        !(e.key === "c" && (e.metaKey || e.ctrlKey)) &&
        !(e.key === "v" && (e.metaKey || e.ctrlKey)) &&
        !(e.key === "x" && (e.metaKey || e.ctrlKey))
      ) {
        e.preventDefault();
      }
      if (e.key === "." || e.key === ",") {
        e.preventDefault();
      }
    }}
    onBlur={(e) => {
      props.onBlur(e);
    }}
    aria-label={props.label}
  />
);

export default Pagination;
